import React from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";
import { FEATURE_TREE } from "../organizations.constants";

const LABEL_SX = { fontSize: 13 };

// The theme only draws "checked". A group with some (not all) of its features
// selected gets a dash on the same dark fill.
const INDETERMINATE_SX = {
  "&.MuiCheckbox-indeterminate": {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-width='2.5' d='M6 11h10'/%3e%3c/svg%3e"), ${linearGradient(
      colors.gradients.dark.main,
      colors.gradients.dark.state
    )}`,
    borderColor: colors.gradients.dark.main,
  },
};

export function FeatureCheckbox({ label, bold, ...props }) {
  return (
    <FormControlLabel
      // Anything that can't be changed (not available, or the tree is read-only)
      // is dimmed so it never looks like an ordinary empty checkbox.
      sx={{
        m: 0,
        opacity: props.disabled ? 0.4 : 1,
        cursor: props.disabled ? "not-allowed" : "pointer",
        "& .MuiFormControlLabel-label": { ml: 0.5 },
      }}
      control={<Checkbox size="small" sx={{ p: 0.75, ...INDETERMINATE_SX }} {...props} />}
      label={
        <SuiTypography variant="button" fontWeight={bold ? "medium" : "regular"} sx={LABEL_SX}>
          {label}
        </SuiTypography>
      }
    />
  );
}

// The features laid out like the sidebar: a feature on its own, or a group (a
// tab such as Reports) with a checkbox that selects everything inside it.
//   selected   keys that are on
//   onChange   called with the new list of keys
//   available  keys that can be picked (null = all); the others are greyed out
//              and never selected, e.g. features the organization doesn't have
//   disabled   the whole tree is read-only
function FeatureTree({ selected, onChange, available, disabled }) {
  const canPick = (key) => !available || available.includes(key);
  const isOn = (key) => canPick(key) && selected.includes(key);

  const toggle = (key) =>
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);

  const toggleGroup = (group) => {
    const keys = group.children.map((child) => child.key).filter(canPick);
    const allOn = keys.every((key) => selected.includes(key));
    onChange(
      allOn ? selected.filter((k) => !keys.includes(k)) : [...new Set([...selected, ...keys])]
    );
  };

  return (
    <SuiBox display="flex" flexDirection="column">
      {FEATURE_TREE.map((item) => {
        if (!item.children) {
          return (
            <FeatureCheckbox
              key={item.key}
              label={item.label}
              disabled={disabled || !canPick(item.key)}
              checked={isOn(item.key)}
              onChange={() => toggle(item.key)}
            />
          );
        }
        const pickable = item.children.filter((child) => canPick(child.key));
        const onCount = pickable.filter((child) => isOn(child.key)).length;
        return (
          <SuiBox key={item.label}>
            <FeatureCheckbox
              bold
              label={item.label}
              disabled={disabled || pickable.length === 0}
              checked={pickable.length > 0 && onCount === pickable.length}
              indeterminate={onCount > 0 && onCount < pickable.length}
              onChange={() => toggleGroup(item)}
            />
            <SuiBox ml={1.75} pl={2} mb={0.5} sx={{ borderLeft: "1px solid #e9ecef" }}>
              {item.children.map((child) => (
                <SuiBox key={child.key}>
                  <FeatureCheckbox
                    label={child.label}
                    disabled={disabled || !canPick(child.key)}
                    checked={isOn(child.key)}
                    onChange={() => toggle(child.key)}
                  />
                </SuiBox>
              ))}
            </SuiBox>
          </SuiBox>
        );
      })}
    </SuiBox>
  );
}

export default FeatureTree;
